import React, { useEffect, useMemo, useState } from 'react'; // eslint-disable-line no-unused-vars
import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import useOperationalStore, { getTimeHealth } from '../platform/store/useOperationalStore';
import { CUSTOMER_TYPES } from '../platform/engine/priorityScore';
import { describeTechnicianStatus, eventAgeLabel } from '../platform/events/technicianStatus';
import FullscreenBtn from './FullscreenBtn';
import './TechnicianView.css';

const client = generateClient();

const STATUS_LABELS = {
  yard:         'Intake',
  triage:       'Triage',
  waiting:      'Diag',
  approval:     'Approval',
  'parts-hold': 'Parts',
  wip:          'Repair',
  'road-test':  'Verify',
  qc:           'QC',
  done:         'Ready',
};

const STATUS_COLORS = {
  yard:         '#6366f1',
  triage:       '#8b5cf6',
  waiting:      '#0ea5e9',
  approval:     '#f59e0b',
  'parts-hold': '#ef4444',
  wip:          '#22c55e',
  'road-test':  '#06b6d4',
  qc:           '#f59e0b',
  done:         '#3b82f6',
};

// Lane mapping mirrors the Flow board columns:
// Diag (triage/waiting) is active work → Working.
// Approval and Parts are waiting states → Holding.
const WORKING  = ['triage', 'waiting', 'wip'];
const NEXT_UP  = ['yard'];
const HOLDING  = ['approval', 'parts-auth', 'parts', 'parts-hold', 'road-test', 'qc'];

// ─── Truck card ───────────────────────────────────────────────────────────────

function TruckCard({ truck, onClick, variant = 'full' }) {
  const h       = getTimeHealth(truck);
  const barPct  = Math.min(100, (h?.pct ?? 0) * 100);
  const barColor = h?.status === 'red' ? '#ef4444' : h?.status === 'amber' ? '#f59e0b' : '#22c55e';
  const accentColor = truck.isHardDown ? '#ef4444' : (STATUS_COLORS[truck.status] || '#1e3058');
  const ctInfo  = CUSTOMER_TYPES.find(c => c.value === truck.customerType);
  const stLabel = STATUS_LABELS[truck.status] || truck.status;

  return (
    <div
      className={`tv-card tv-card--${variant}${truck.isHardDown ? ' tv-card--fd' : ''}`}
      style={{ borderLeftColor: accentColor }}
      onClick={onClick}
    >
      <div className="tv-card-top">
        <span className="tv-card-ro">{truck.repairOrder}</span>
        {truck.isHardDown && <span className="tv-card-fd-badge">FLEET DOWN</span>}
      </div>
      <div className="tv-card-company">{truck.companyName}</div>
      {variant === 'full' && truck.truckNumber && <div className="tv-card-unit">#{truck.truckNumber}</div>}
      <div className="tv-card-bottom">
        <span className="tv-card-status">{stLabel}</span>
        {variant === 'full' && ctInfo && (
          <span className="tv-card-ctype" style={{ color: ctInfo.color, borderColor: ctInfo.color + '55' }}>
            {ctInfo.label}
          </span>
        )}
      </div>
      {h && (
        <div className="tv-bar-wrap">
          <div className="tv-bar-fill" style={{ width: `${barPct}%`, background: barColor }} />
        </div>
      )}
    </div>
  );
}

// ─── Section ─────────────────────────────────────────────────────────────────
// working: 2-3 detail cards side by side · next: one compact card (+N)
// holding: slim tickets compressing horizontally, Flow-board style

function Section({ trucks, className, onClick, variant = 'full', limit = null }) {
  const isEmpty = trucks.length === 0;
  const shown = limit ? trucks.slice(0, limit) : trucks;
  const extra = trucks.length - shown.length;
  return (
    <div className={`tv-section ${className}${isEmpty ? ' tv-section--empty' : ''}`}>
      {isEmpty
        ? <span className="tv-empty-label">—</span>
        : (
          <>
            {shown.map(t => <TruckCard key={t.id} truck={t} variant={variant} onClick={() => onClick(t.id)} />)}
            {extra > 0 && <span className="tv-more" title={`${extra} more queued`}>+{extra}</span>}
          </>
        )
      }
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TechnicianView() {
  const trucks         = useOperationalStore(s => s.trucks);
  const technicians    = useOperationalStore(s => s.technicians);
  const setSelected    = useOperationalStore(s => s.setSelectedTruckId);
  const [latestEvents, setLatestEvents] = useState({});

  const active = trucks.filter(t => !['completed', 'voided'].includes(t.status));
  const techs  = useMemo(() => technicians
    .filter(t => t.active !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [technicians]);

  useEffect(() => {
    let cancelled = false;

    async function loadLatestEvents() {
      if (!techs.length || !queries.operationEventsByTechnician) {
        setLatestEvents({});
        return;
      }

      const results = await Promise.all(techs.map(async tech => {
        const lookupIds = [...new Set([tech.id, tech.number].filter(Boolean).map(String))];
        const eventLists = await Promise.all(lookupIds.map(async technicianId => {
          try {
            const result = await client.graphql({
              query: queries.operationEventsByTechnician,
              variables: { technicianId, sortDirection: 'DESC', limit: 1 },
            });
            return result.data?.operationEventsByTechnician?.items || [];
          } catch (err) {
            console.warn('Could not load technician event:', err.message);
            return [];
          }
        }));
        const latest = eventLists.flat().sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0] || null;
        return [tech.id, latest];
      }));

      if (!cancelled) setLatestEvents(Object.fromEntries(results));
    }

    loadLatestEvents();
    const timer = setInterval(loadLatestEvents, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [techs]);

  function trucksBelongingTo(tech, statuses) {
    return active.filter(t =>
      statuses.includes(t.status) &&
      ((t.techNumbers || []).includes(tech.number) || t.techNumber === tech.number)
    ).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }

  function loadCount(tech) {
    return active.filter(t =>
      (t.techNumbers || []).includes(tech.number) || t.techNumber === tech.number
    ).length;
  }

  if (!techs.length) {
    return (
      <div className="tv-root">
        <div className="tv-no-techs">No active team members</div>
      </div>
    );
  }

  const totalAssigned = new Set(
    active.filter(t => techs.some(tech => (t.techNumbers || []).includes(tech.number) || t.techNumber === tech.number)).map(t => t.id)
  ).size;

  return (
    <div className="tv-root">

      {/* Shared toolbar pattern (same as board views) */}
      <div className="vtb">
        <div className="vtb-left">
          <h1 className="tv-title">Team</h1>
          <div className="vtb-divider" />
          <div className="vtb-stat"><span className="vtb-stat-val">{techs.length}</span><span className="vtb-stat-lbl">Techs</span></div>
          <div className="vtb-stat"><span className="vtb-stat-val">{totalAssigned}</span><span className="vtb-stat-lbl">Assigned ROs</span></div>
          <div className="vtb-stat"><span className="vtb-stat-val">{active.length - totalAssigned}</span><span className="vtb-stat-lbl">Unassigned</span></div>
        </div>
        <div className="vtb-right">
          <button
            className="tv-popout-btn"
            onClick={() => window.open(window.location.origin + '/flow?view=tech-view', '_blank', 'noopener')}
            title="Open Team View in new window"
            aria-label="Open Team View in new window"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </button>
          <FullscreenBtn />
        </div>
      </div>

      <div className="tv-col-headers">
        <div className="tv-col-header-spacer" />
        <span className="tv-col-header tv-col-header--working">Working</span>
        <span className="tv-col-header tv-col-header--next">Next Up</span>
        <span className="tv-col-header tv-col-header--holding">Holding</span>
      </div>

      <div className="tv-lanes">
        {techs.map(tech => {
          const working = trucksBelongingTo(tech, WORKING);
          const nextUp  = trucksBelongingTo(tech, NEXT_UP);
          const holding = trucksBelongingTo(tech, HOLDING);
          const load    = loadCount(tech);
          const techStatus = describeTechnicianStatus(latestEvents[tech.id], load);
          const age = eventAgeLabel(techStatus.event?.timestamp);

          return (
            <div key={tech.id} className="tv-row">
              <div className="tv-tech-cell">
                <span className="tv-tech-name">{tech.name}</span>
                <span className="tv-tech-num">#{tech.number}</span>
                <span className={`tv-tech-state tv-tech-state--${techStatus.tone}`}>
                  {techStatus.label}
                </span>
                {age && <span className="tv-tech-event-age">{age} ago</span>}
                <span className="tv-tech-load">{load} truck{load !== 1 ? 's' : ''}</span>
              </div>
              <Section trucks={working} className="tv-section--working" onClick={setSelected} variant="full" />
              <Section trucks={nextUp}  className="tv-section--next"    onClick={setSelected} variant="compact" limit={1} />
              <Section trucks={holding} className="tv-section--holding" onClick={setSelected} variant="compact" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
